import React, { useState, useEffect } from 'react';

const EmployeeLoanDetailsReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportParams, setReportParams] = useState({
    asOfDate: "",
    branch: ""
  });

  // Extract parameters from URL or use default values
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      asOfDate: urlParams.get('asOfDate') || new Date().toISOString().split('T')[0],
      branch: urlParams.get('branch') || 'ALL',
    };
    setReportParams(params);
  }, []);

  // Function to call the API (replace with your actual API endpoint)
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams(reportParams);
      // Replace with your actual API endpoint
      const response = await fetch(`https://www.nissi-india.com/api/employee_loan_details?${queryParams}`);
      console.log("params:", queryParams);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("response:", data);
      setReportData(data.data || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts or parameters change
  useEffect(() => {
    fetchReportData();
  }, [reportParams]);

  // Sample data to match the screenshot
  const sampleData = [
    { empCode: "NL1-005", empName: "RAMAN", loanAmount: 15000.00, deductionAmount: 1500.00, balanceAmount: 13500.00, totalMonth: 10, balanceMonth: 9, location: "HEAD OFFICE" },
    { empCode: "PA-003", empName: "BRAJESH SENWAL", loanAmount: 25000.00, deductionAmount: 2500.00, balanceAmount: 22500.00, totalMonth: 10, balanceMonth: 9, location: "HEAD OFFICE" },
    { empCode: "FA-002", empName: "ASHISH KUMAR", loanAmount: 1000.00, deductionAmount: 5.00, balanceAmount: 995.00, totalMonth: 200, balanceMonth: 199, location: "HEAD OFFICE" },
    { empCode: "2503", empName: "NITHISH", loanAmount: 25000.00, deductionAmount: 5000.00, balanceAmount: 15000.00, totalMonth: 5, balanceMonth: 3, location: "HEAD OFFICE" }
  ];

  // Use sample data if no API data is available
  const displayData = reportData.length > 0 ? reportData : sampleData;

  // Calculate total amount
  const totalAmount = displayData.reduce((sum, item) => sum + (parseFloat(item.balanceAmount) || 0), 0);

  return (
    <div className="max-w-5xl mx-auto p-4 bg-white font-sans text-sm print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="border-b border-black flex">
          {/* Logo Section */}
          <div className="w-32 border-r border-black p-2 flex items-center justify-center">
            <div className="bg-black text-white px-2 py-1 text-xs font-bold text-center">
              <div>NISSI</div>
              <div>INFOTECH PVT LTD</div>
            </div>
          </div>
          
          {/* Company Info Section */}
          <div className="flex-1 p-3">
            <div className="text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-xs">
                <div>Nissi</div>
                <div className="font-bold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony, kavundampalayam,641600, Tamil Nadu, India.</div>
                <div>Phone No: 9003744644 infraconsoft-implementation@nissi</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Report Title */}
        <div className="text-center py-2 border-b border-black font-bold">
          EMPLOYEE LOAN DETAILS
        </div>
      </div>

      {/* Loading/Error States */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div>{error}</div>
          <button 
            onClick={fetchReportData}
            className="mt-2 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table Section */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black bg-gray-100">
              <th className="border-r border-black px-2 py-1 w-8 text-center">S.No</th>
              <th className="border-r border-black px-2 py-1 w-16 text-center">EMP Code</th>
              <th className="border-r border-black px-2 py-1 w-24 text-center">EMP Name</th>
              <th className="border-r border-black px-2 py-1 w-20 text-center">
                <div>Loan</div>
                <div>Amount</div>
              </th>
              <th className="border-r border-black px-2 py-1 w-20 text-center">
                <div>Deduction</div>
                <div>Amount</div>
              </th>
              <th className="border-r border-black px-2 py-1 w-20 text-center">
                <div>Balance</div>
                <div>Amount</div>
              </th>
              <th className="border-r border-black px-2 py-1 w-16 text-center">
                <div>Total</div>
                <div>Month</div>
              </th>
              <th className="border-r border-black px-2 py-1 w-16 text-center">
                <div>Balance</div>
                <div>Month</div>
              </th>
              <th className="px-2 py-1 w-20 text-center">Location</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((item, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-black px-2 py-1 text-center">{index + 1}</td>
                <td className="border-r border-black px-2 py-1">{item.empCode || ""}</td>
                <td className="border-r border-black px-2 py-1">{item.empName || ""}</td>
                <td className="border-r border-black px-2 py-1 text-right">
                  {Number(item.loanAmount || 0).toFixed(2)}
                </td>
                <td className="border-r border-black px-2 py-1 text-right">
                  {Number(item.deductionAmount || 0).toFixed(2)}
                </td>
                <td className="border-r border-black px-2 py-1 text-right">
                  {Number(item.balanceAmount || 0).toFixed(2)}
                </td>
                <td className="border-r border-black px-2 py-1 text-center">
                  {item.totalMonth || ""}
                </td>
                <td className="border-r border-black px-2 py-1 text-center">
                  {item.balanceMonth || ""}
                </td>
                <td className="px-2 py-1">{item.location || "HEAD OFFICE"}</td>
              </tr>
            ))}

            {/* Total Row */}
            <tr className="border-t-2 border-black font-bold bg-gray-50">
              <td colSpan="5" className="border-r border-black px-2 py-1 text-center">
                Total Amount
              </td>
              <td className="border-r border-black px-2 py-1 text-right">
                {totalAmount.toFixed(2)}
              </td>
              <td className="border-r border-black px-2 py-1"></td>
              <td className="border-r border-black px-2 py-1"></td>
              <td className="px-2 py-1"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Report Footer */}
      <div className="mt-4 text-xs text-gray-600 border border-gray-300 p-2 bg-gray-50 print:hidden">
        Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body { margin: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          @page { 
            margin: 0.5in; 
          }
        }
      `}</style>
    </div>
  );
};

export default EmployeeLoanDetailsReport;